/**
 * Trade Desk data layer shared by the Simple and Pro desks: response types
 * (mirror backend/src/api/routes/desk.ts), formatting helpers, stage labels
 * and the useDesk() hook that scans, re-scans after each 1h close and logs trades.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSettings } from "../../hooks/useSettings";
import { CHASE_ATR, type ChartSignal, type DeskCheck, type SetupPlan, type SetupZone } from "../chart/chartSignals";

// ─── types (mirror backend/src/api/routes/desk.ts) ───────────────────────────

export type Stage = "no-bias" | "stalking" | "armed" | "ready" | "blocked" | "signal";
export type Status = "met" | "pending" | "failed";

export interface ChecklistItem {
  key: string; label: string; status: Status; detail: string; hard: boolean; plain: string;
  /** Met, with a caution: allowed on different terms (Against BTC: half risk). */
  warn?: boolean;
}
export interface Checklist {
  archetype: "trend_pullback" | "range_fade";
  direction: "LONG" | "SHORT";
  stage: Exclude<Stage, "blocked" | "signal">;
  items: ChecklistItem[];
  metCount: number;
  total: number;
  waitingFor: string;
  levels: {
    price: number; ema20: number; ema50: number; atr: number; rsi: number;
    zoneLow: number | null; zoneHigh: number | null; invalidation: number | null;
    rangeHigh: number | null; rangeLow: number | null; volumeRatio: number;
    /** The engine's plan if the trigger closed now (backend preTriggerChecks); only on coins without a signal. */
    plan?: {
      entry: number; stop: number; tp1: number; tp2: number;
      tp1Exact: boolean; riskReward: number; costR: number;
      /** Where an entry passes the engine's reward and fee rules: the zone to draw. */
      zoneLow?: number | null; zoneHigh?: number | null;
    } | null;
  };
}
export interface Plan {
  entry: number; stopLoss: number; tp1: number; tp2: number; riskReward: number;
  stopPct: number; tp1Pct: number; tp2Pct: number; costR: number; timeStopHours: number;
  position: { units: number; notional: number; riskAmount: number; riskPercent: number; drawdownMultiplier: number };
}
export interface DeskSignal {
  action: "LONG" | "SHORT"; score: number; confidence: string;
  setupArchetype: string; setupQuality: string; entryTrigger: string; thesis: string;
  reasons: string[]; warnings: string[]; regime: string; winRate: number; totalTrades: number; plan: Plan;
  /** Against BTC's trend: sent at half risk (the plan is already sized for it). */
  againstBtc?: boolean;
}
/**
 * Share of the usual risk an Against-BTC signal trades at (backend
 * evaluateSignal AGAINST_BTC_RISK): the engine lets a trade against BTC's
 * strong trend through at half size instead of blocking it.
 */
export const AGAINST_BTC_RISK = 0.5;
export interface DeskRow {
  symbol: string; price: number; lastCloseAt: number;
  htfBias: { direction: "bullish" | "bearish" | "neutral"; confidence: number };
  btcTrend: string; checklist: Checklist; signal: DeskSignal | null;
  rejection: { reason: string; detail?: string; direction?: "LONG" | "SHORT" } | null;
  /** A signal already sent to Telegram for this coin and still open, if any. */
  openSignal?: OpenSignal | null;
  /** Plain-English "can I trade this now?" (backend services/signals/deskVerdict.ts). */
  verdict: Verdict;
}
export type VerdictStatus = "enter" | "almost" | "wait" | "no-trade";

/** Signal already published (Telegram), with its live status (backend openSignalStatus.ts). */
export type OpenSignalState = "valid" | "missed" | "expired" | "in-profit" | "stopped";
export interface OpenSignal {
  id: string; symbol: string; action: "LONG" | "SHORT"; sentAt: number; price: number;
  state: OpenSignalState; headline: string; explanation: string; next: string; plan: Plan;
  /** Sent against BTC's trend: the plan is sized at half risk. */
  againstBtc?: boolean;
}
export const OPEN_SIGNAL_META: Record<OpenSignalState, { label: string; color: ChipColor }> = {
  valid: { label: "Live", color: "success" },
  "in-profit": { label: "In profit", color: "accent" },
  missed: { label: "Missed", color: "warning" },
  expired: { label: "Expired", color: "default" },
  stopped: { label: "Stopped", color: "danger" },
};

/** "58 min ago" / "3 h ago". */
export function ago(ms: number): string {
  const m = Math.max(0, Math.round((Date.now() - ms) / 60000));
  return m < 60 ? `${m} min ago` : `${Math.round(m / 60)} h ago`;
}
export interface Verdict {
  status: VerdictStatus; stage: Stage; headline: string; explanation: string; next: string;
  /** The engine rule that blocked a fully-passed checklist (backend deskVerdict.ts). */
  engineCheck?: { label: string; plain: string; detail: string };
}

/**
 * The desk's answer for the candle it last checked, for the chart: its Ready
 * plan, or null for "no trade". The chart uses it to agree with this desk.
 */
export function deskCheckFor(row: DeskRow | null | undefined): DeskCheck | undefined {
  if (!row) return undefined;
  const s = row.signal;
  // The don't-chase price the chart draws: the same 0.3 ATR rule as Telegram and the Missed status.
  const atr = row.checklist.levels.atr;
  const chase = s && atr > 0 ? (s.action === "LONG" ? s.plan.entry + CHASE_ATR * atr : s.plan.entry - CHASE_ATR * atr) : null;
  return {
    closeSec: Math.round(row.lastCloseAt / 1000),
    plan: s && {
      action: s.action, entry: s.plan.entry, stopLoss: s.plan.stopLoss, tp1: s.plan.tp1, tp2: s.plan.tp2,
      riskReward: s.plan.riskReward, chase, archetype: s.setupArchetype, quality: s.setupQuality,
      trigger: s.entryTrigger, thesis: s.thesis, score: s.score, timeStopHours: s.plan.timeStopHours,
      againstBtc: s.againstBtc ?? false,
    },
  };
}

/**
 * What the coin is waiting for, for the chart: its entry zone and stop, only
 * while the desk says Waiting or Almost (a Ready coin's trade is drawn as a
 * signal instead; No setup and Blocked have nothing to wait for).
 */
export function deskSetupFor(row: DeskRow | null | undefined): SetupZone | null {
  if (!row || (row.verdict.stage !== "stalking" && row.verdict.stage !== "armed")) return null;
  const { direction, archetype, levels, items } = row.checklist;
  if (levels.zoneLow == null || levels.zoneHigh == null) return null;
  // The pullback zone (EMAs ± 1.5 ATR) can reach past the stop. Past the stop
  // the setup is void (structure broke), so only the part before it is a zone.
  const stop = levels.invalidation;
  // The backend trims the zone to entries the engine would accept (fees and
  // reward); without that, clip it at the stop like before.
  const band = levels.plan?.zoneLow != null && levels.plan.zoneHigh != null ? levels.plan : null;
  const low = band ? band.zoneLow! : direction === "LONG" && stop != null ? Math.max(levels.zoneLow, stop) : levels.zoneLow;
  const high = band ? band.zoneHigh! : direction === "SHORT" && stop != null ? Math.min(levels.zoneHigh, stop) : levels.zoneHigh;
  if (low >= high) return null;
  const { met, total } = ruleCounts(row);
  return {
    direction,
    low,
    high,
    stop,
    fromSec: Math.round(row.lastCloseAt / 1000),
    armed: row.verdict.stage === "armed",
    needsPattern: items.some(it => it.key === "engine" && it.status === "pending"),
    plan: setupPlanFor(direction, archetype, row.price, low, high, stop, row.verdict.stage === "armed", levels),
    details: {
      coin: row.symbol.replace("USDT", ""),
      archetype,
      price: row.price,
      rulesMet: met,
      rulesTotal: total,
      // The trigger and the engine's score name the exact candle needed; the rest read best in plain words.
      // A pending score is the trigger's own requirement (its row already says which candle), so list it once.
      waitingFor: [...new Set(items
        .filter(it => it.status !== "met")
        .filter(it => !(it.key === "engine" && it.status === "pending" && items.some(t => t.key === "trigger" && t.status !== "met")))
        .map(it => ((it.key === "trigger" || it.key === "engine") && it.detail ? it.detail : it.plain || it.label)))],
      next: row.verdict.next,
    },
  };
}

/** A signal needs reward to target 1 of at least this × the risk (backend evaluateSignal MIN_RR). */
export const MIN_RR = 1.5;

/**
 * The plan a signal in this zone would get. The backend now sends the
 * engine's own plan (levels.plan, from the same target formulas the engine
 * uses, with the nearest structural level for target 1); the local estimate
 * below is only a fallback. It enters at the trigger candle's close. Almost:
 * the forming candle is the trigger candidate, so price now. Waiting: price
 * must come to the zone first, so the zone's near edge.
 */
export function setupPlanFor(
  direction: "LONG" | "SHORT",
  archetype: Checklist["archetype"],
  price: number,
  low: number,
  high: number,
  stop: number | null,
  armed: boolean,
  levels: Pick<Checklist["levels"], "atr" | "rangeHigh" | "rangeLow" | "plan">,
): SetupPlan | null {
  const ep = levels.plan;
  if (ep) {
    return {
      entry: ep.entry, stop: ep.stop, tp1: ep.tp1, tp2: ep.tp2, rr: ep.riskReward,
      tp1Exact: true,
      tp1Kind: archetype === "range_fade" ? "range" : ep.tp1Exact ? "structure" : "rr",
    };
  }
  if (stop == null) return null;
  const long = direction === "LONG";
  const entry = armed ? price : Math.min(high, Math.max(low, price));
  // Already past the stop: no trade to plan.
  if (long ? entry <= stop : entry >= stop) return null;
  const risk = Math.abs(entry - stop);
  let tp1: number;
  let tp2: number;
  let tp1Exact: boolean;
  if (archetype === "range_fade" && levels.rangeHigh != null && levels.rangeLow != null) {
    // Range middle, then near the far edge.
    tp1 = (levels.rangeHigh + levels.rangeLow) / 2;
    tp2 = long ? levels.rangeHigh - 0.25 * levels.atr : levels.rangeLow + 0.25 * levels.atr;
    tp1Exact = true;
  } else {
    // The next resistance (support for a short), which must be ≥ 1.5× the risk away.
    tp1 = long ? entry + MIN_RR * risk : entry - MIN_RR * risk;
    tp2 = long ? entry + 2.5 * risk : entry - 2.5 * risk;
    tp1Exact = false;
  }
  return { entry, stop, tp1, tp1Exact, tp2, rr: Math.abs(tp1 - entry) / risk };
}

/** Rules passed / total, counting the engine's failed rule when it blocked the trade. */
export function ruleCounts(row: DeskRow): { met: number; total: number } {
  const c = row.checklist;
  return { met: c.metCount, total: c.total + (row.verdict.engineCheck ? 1 : 0) };
}
export interface DeskResponse {
  success: boolean; error?: string;
  config: { signalTf: string; biasTf: string; archetypes: string[]; minQuality: string[]; minRR: number; maxCostR: number };
  clock: { now: number; nextCandleClose: number };
  market: { btcTrend: string; btcBias4h: string; macro: { sentiment: string; score: number; altcoinSafe: boolean; summary: string } | null };
  ready: DeskRow[]; watching: DeskRow[]; scanned: number;
  openSignals: OpenSignal[];
  /** Today's coin list (backend dailyUniverse.ts): picked once per UTC day. */
  universe: Universe;
}

export interface Universe {
  day: string; fallback: boolean;
  included: { symbol: string; volumeM: number; atrPct: number; btcCorr: number; why: string }[];
  excluded: { symbol: string; reason: string }[];
}

/** "$1.07B" / "$412M": a coin's 24h trading volume. */
export function volumeText(volumeM: number): string {
  return volumeM >= 1000 ? `$${(volumeM / 1000).toFixed(2)}B` : `$${Math.round(volumeM)}M`;
}

// ─── helpers ─────────────────────────────────────────────────────────────────

/**
 * Decimals for a price, about five significant digits, so a close can be
 * compared with a trigger level (NEAR 4.879, XRP 1.5069, DOGE 0.09545). One
 * rule for the desk, the chart's price axis and its tags; the backend's
 * shared/utils/format.ts (desk text, Telegram) uses the same one.
 */
export function pricePrecision(n: number): number {
  const a = Math.abs(n);
  return a >= 10 ? 2 : a >= 0.1 ? 4 : a >= 0.01 ? 5 : a >= 0.001 ? 6 : 8;
}
/** A price as text: separators from 1,000, trailing zeros trimmed past 2 decimals. */
export function fmt(n: number | null | undefined): string {
  if (n == null || !isFinite(n)) return "—";
  if (Math.abs(n) >= 1000) return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return n.toFixed(pricePrecision(n)).replace(/(\.\d\d\d*?)0+$/, "$1");
}
export function pct(n: number): string { return `${n > 0 ? "+" : ""}${n.toFixed(2)}%`; }
export function archetypeLabel(a: string) { return a.replace(/_/g, " "); }
/** Where each setup is explained: the same words as Telegram's "Learn:" line (backend signals/playbookRef.ts). */
export const PLAYBOOK_REF: Record<string, string> = {
  trend_pullback: "Playbook › 2. The setups › Trend pullback",
  range_fade: "Playbook › 2. The setups › Range fade",
  breakout_retest: "Playbook › 2. The setups › Breakout retest",
};
/**
 * "If this 1-hour candle closed now" (backend services/signals/closePreview.ts):
 * the engine run on the candle still forming. Display only: signals come from
 * closed candles, the preview just shows the trigger taking shape.
 */
export interface ClosePreview {
  /** When the forming candle closes (ms). */
  closesAt: number;
  candle: { open: number; high: number; low: number; close: number };
  direction: "LONG" | "SHORT";
  /** Trend pullback: a plain close past this is a trigger (the last closed candle's high / low). */
  triggerLevel: number | null;
  /** The trigger the engine sees on the forming candle so far, in words, or null. */
  trigger: string | null;
  /** Volume so far, on pace for this multiple of a normal hour. */
  volumePace: number;
  /** The signal the engine would send if the candle closed as it is now. */
  signal: {
    action: "LONG" | "SHORT"; entry: number; stopLoss: number; tp1: number; tp2: number;
    riskReward: number; score: number; trigger: string;
    /** Against BTC's trend: it would go out at half risk. */
    againstBtc?: boolean;
  } | null;
  /** Why there would be no signal, in plain words. */
  blocker: string | null;
}

const PREVIEW_POLL_MS = 30_000;

/** Polls the close preview for these coins every 30 s while mounted. */
export function useClosePreview(symbols: string[]): Record<string, ClosePreview | null> {
  const key = [...new Set(symbols)].sort().join(",");
  const [previews, setPreviews] = useState<Record<string, ClosePreview | null>>({});
  useEffect(() => {
    if (!key) { setPreviews({}); return; }
    let cancelled = false;
    const load = () =>
      fetch(`/api/trading-signals/close-preview?symbols=${encodeURIComponent(key)}`)
        .then(r => r.json())
        .then(d => { if (!cancelled && d.success) setPreviews(d.previews ?? {}); })
        .catch(() => { /* keep the last preview; the next poll retries */ });
    load();
    const id = setInterval(load, PREVIEW_POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [key]);
  return previews;
}

export function useCountdown(target: number | null) {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    if (!target) return;
    const tick = () => setLeft(Math.max(0, target - Date.now()));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [target]);
  const m = Math.floor(left / 60000);
  const s = Math.floor((left % 60000) / 1000);
  return { left, label: `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}` };
}

export type ChipColor = "default" | "accent" | "success" | "warning" | "danger";
// Beginner-first names; the hint explains each in one sentence. Colour is kept
// for stages that need action (Ready, Almost, Signal sent): Waiting is calm grey
// so the few coins that matter stand out from the many that don't.
export const STAGE_META: Record<Stage, { label: string; color: ChipColor; hint: string }> = {
  ready:    { label: "Ready",    color: "success", hint: "A real trade: every rule passed, including the engine's. See the Ready tab." },
  armed:    { label: "Almost",   color: "warning", hint: "Price is in the entry zone. Watch the next 1-hour candle close." },
  signal:   { label: "Signal sent", color: "accent",  hint: "A Telegram signal for this coin is still open. Its status is this coin's answer." },
  blocked:  { label: "Blocked",  color: "danger",  hint: "The checklist passed, but the engine rejected it. Don't trade." },
  stalking: { label: "Waiting",  color: "default",  hint: "The trend is set, but price hasn't reached the entry zone yet." },
  "no-bias":{ label: "No setup", color: "default", hint: "A required condition is missing. Nothing to do." },
};

export const VERDICT_TONE: Record<VerdictStatus, string> = {
  enter: "text-profit",
  almost: "text-warning",
  wait: "text-foreground/85",
  "no-trade": "text-muted",
};

// ─── data hook ───────────────────────────────────────────────────────────────

/**
 * Scans the desk (GET /api/trading-signals/desk) for the user's coins, re-scans
 * ~20s after every 1h close (the only moment a new signal can appear), and logs
 * taken trades. One instance per mounted desk view.
 */
export function useDesk() {
  const { settings } = useSettings();
  const [data, setData] = useState<DeskResponse | null>(null);
  // Today's coins (picked once a day, the same list the Telegram scanner
  // watches) arrive in milliseconds, while scanning them takes seconds: fetched
  // first, so the loading screen already shows the real coins.
  const [picked, setPicked] = useState<Universe | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetch("/api/trading-signals/universe")
      .then(r => r.json())
      .then(j => { if (!cancelled && j.success) setPicked(j.universe); })
      .catch(() => { /* the scan brings the list too */ });
    return () => { cancelled = true; };
  }, []);
  const universe = data?.universe ?? picked;
  const coins = useMemo(() => universe?.included.map(c => c.symbol.replace("USDT", "")) ?? [], [universe]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [logging, setLogging] = useState<string | null>(null);
  const inflight = useRef<AbortController | null>(null);

  const scan = useCallback(async () => {
    // A new scan supersedes any running one (StrictMode double-mount, a
    // manual click during the hourly auto-rescan, etc.).
    inflight.current?.abort();
    const ctrl = new AbortController();
    inflight.current = ctrl;
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams({
        accountSize: String(settings.accountSize),
        riskPercent: String(settings.riskPercent),
      });
      const res = await fetch(`/api/trading-signals/desk?${qs}`, { signal: ctrl.signal });
      const text = await res.text();
      if (!text) throw new Error("Empty response from backend — it may be restarting. Press Scan again.");
      const json = JSON.parse(text) as DeskResponse;
      if (!json.success) throw new Error(json.error || "scan failed");
      setData(json);
    } catch (e) {
      if (e instanceof DOMException && e.name === "AbortError") return;
      setError(e instanceof Error ? e.message : "Scan failed — check backend connection");
    } finally {
      if (inflight.current === ctrl) setLoading(false);
    }
  }, [settings.accountSize, settings.riskPercent]);

  useEffect(() => () => inflight.current?.abort(), []);

  useEffect(() => { scan(); }, [scan]);

  // Re-scan shortly after every 1h close — that's the only moment a new
  // signal can appear (we evaluate closed candles only).
  const countdown = useCountdown(data?.clock.nextCandleClose ?? null);
  useEffect(() => {
    if (!data) return;
    const delay = Math.max(5000, data.clock.nextCandleClose - Date.now() + 20_000);
    const id = setTimeout(scan, delay);
    return () => clearTimeout(id);
  }, [data, scan]);

  const logTrade = useCallback(async (row: DeskRow) => {
    const s = row.signal!;
    setLogging(row.symbol);
    try {
      const res = await fetch(`/api/tracked-trades`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          symbol: row.symbol,
          direction: s.action,
          entryPrice: s.plan.entry,
          stopLoss: s.plan.stopLoss,
          takeProfit1: s.plan.tp1,
          takeProfit2: s.plan.tp2 || undefined,
          margin: s.plan.position.units,
          confluenceScore: s.score,
          setupType: `${s.setupArchetype}:${s.setupQuality}`,
          entryType: "signal",
          mode: "real",
          timeStopHours: s.plan.timeStopHours,
          // The plan is already sized at half risk against BTC; the daily limit counts it at that weight.
          riskScale: s.againstBtc ? AGAINST_BTC_RISK : 1,
        }),
      });
      if (res.ok) window.dispatchEvent(new Event(TRADES_CHANGED));
      setToast(res.ok ? { msg: `${row.symbol} logged`, ok: true } : { msg: "Failed to log trade", ok: false });
    } catch {
      setToast({ msg: "Failed to log trade — check connection", ok: false });
    } finally {
      setLogging(null);
      setTimeout(() => setToast(null), 3000);
    }
  }, []);

  /** Log a trade taken from a signal that was sent to Telegram. */
  const logOpenSignal = useCallback(async (o: OpenSignal) => {
    setLogging(o.symbol);
    try {
      const res = await fetch(`/api/tracked-trades`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          symbol: o.symbol, direction: o.action, entryPrice: o.plan.entry, stopLoss: o.plan.stopLoss,
          takeProfit1: o.plan.tp1, takeProfit2: o.plan.tp2 || undefined, margin: o.plan.position.units,
          setupType: `telegram:${o.id}`,
          entryType: "signal",
          mode: "real",
          timeStopHours: o.plan.timeStopHours,
          riskScale: o.againstBtc ? AGAINST_BTC_RISK : 1,
        }),
      });
      if (res.ok) window.dispatchEvent(new Event(TRADES_CHANGED));
      setToast(res.ok ? { msg: `${o.symbol} logged`, ok: true } : { msg: "Failed to log trade", ok: false });
    } catch {
      setToast({ msg: "Failed to log trade — check connection", ok: false });
    } finally {
      setLogging(null);
      setTimeout(() => setToast(null), 3000);
    }
  }, []);

  return {
    settings, coins, universe, data, loading, error, scan, countdown, logTrade, logOpenSignal, logging, toast,
    openSignals: data?.openSignals ?? [],
    ready: data?.ready ?? [],
    watching: data?.watching ?? [],
    firstLoad: loading && !data,
  };
}

// ─── the user's own trades (backend services/tradeTracking.ts) ──────────────

/** How a trade was entered: what the stats compare to improve the system. */
export type EntryType = "signal" | "early" | "own";
/** "paper" = practice: tracked like any trade, no real money. */
export type TradeMode = "real" | "paper";

export const ENTRY_TYPE_META: Record<EntryType, { label: string; hint: string }> = {
  signal: { label: "Followed a signal", hint: "Entered on the engine's signal (Ready, or still live on Telegram)." },
  early: { label: "Early, before the signal", hint: "Entered in a Waiting/Almost zone before the engine confirmed it." },
  own: { label: "Own idea", hint: "Entered without an engine setup on that side." },
};

export interface TrackedTrade {
  id: string;
  symbol: string;
  direction: "LONG" | "SHORT" | "WATCH";
  entry_price: number;
  stop_loss: number;
  take_profit_1: number;
  take_profit_2: number | null;
  status: "active" | "tp1_hit" | "tp2_hit" | "stopped" | "invalidated" | "cancelled";
  close_reason: string | null;
  exit_price: number | null;
  current_price: number | null;
  created_at: number;
  closed_at: number | null;
  tp1_hit_at: number | null;
  /** Net R: final once closed, marked to market while open. */
  r_multiple: number | null;
  /** The daily limit this real trade broke when it was logged (desk LOCKED), else null. */
  rule_break?: string | null;
  entry_type: EntryType | null;
  mode: TradeMode | null;
  setup_type: string | null;
  time_stop_hours: number | null;
  /** "auto": an engine signal tracked automatically (practice); otherwise logged by you. */
  source?: string | null;
  signal_id?: string | null;
  /** JSON "why it won or lost", once closed (backend tradeReview.ts). */
  review?: string | null;
  /** Share of the usual risk it was sized for: 0.5 on an Against-BTC signal. */
  risk_scale?: number | null;
}

export interface TradeStats {
  taken: number; open: number; won: number; lost: number; even: number;
  /** won ÷ closed, %; null before anything closed. */
  winRate: number | null;
  netR: number;
  openR: number;
}
export type TradeSource = "auto" | "manual";
export interface TradeSummary {
  all: TradeStats; byType: Record<EntryType, TradeStats>; byMode: Record<TradeMode, TradeStats>;
  /** The engine's auto-tracked signals vs your own trades, for the same period. */
  bySource: Record<TradeSource, TradeStats>;
}
export const isAutoTrade = (t: Pick<TrackedTrade, "source">) => t.source === "auto";
export type SummaryPeriod = "today" | "7d" | "30d";

/** Mirrors the backend: older rows logged from a signal card count as "signal". */
export function entryTypeOf(t: Pick<TrackedTrade, "entry_type" | "setup_type">): EntryType {
  if (t.entry_type === "signal" || t.entry_type === "early" || t.entry_type === "own") return t.entry_type;
  return /^(telegram:|trend_pullback|range_fade|breakout_retest)/.test(t.setup_type ?? "") ? "signal" : "own";
}

/** Open: the tracker hasn't closed it and the user hasn't recorded an exit. */
export function isTradeOpen(t: Pick<TrackedTrade, "status" | "exit_price">): boolean {
  return (t.status === "active" || t.status === "tp1_hit") && t.exit_price == null;
}

/** A logged trade, drawn on the chart like a signal ("YOU BUY"). */
/** Where a closed trade exited; older rows didn't always record it. */
const exitOf = (t: TrackedTrade) =>
  t.exit_price ?? (t.status === "stopped" ? t.stop_loss : t.status === "tp2_hit" ? t.take_profit_2 ?? t.take_profit_1 : null);

/** A closed trade's net R. Older rows have none: estimated from the exit, like the backend's summary. */
export function closedTradeR(t: TrackedTrade): number | null {
  if (t.r_multiple != null) return t.r_multiple;
  const exit = exitOf(t);
  const risk = Math.abs(t.entry_price - t.stop_loss);
  if (t.direction === "WATCH" || exit == null || !(risk > 0)) return null;
  return ((t.direction === "SHORT" ? -1 : 1) * (exit - t.entry_price)) / risk;
}

export function chartSignalFromTrade(t: TrackedTrade): ChartSignal | null {
  if (t.direction === "WATCH" || (t.status === "cancelled" && t.exit_price == null)) return null;
  const open = isTradeOpen(t);
  const result: ChartSignal["result"] = open
    ? "open"
    : t.status === "tp2_hit" ? "full-win"
    : t.status === "stopped" && t.close_reason === "be" ? "be-stop"
    : t.status === "stopped" && t.close_reason === "time" ? "time-stop"
    : t.status === "stopped" ? "stopped"
    : "closed";
  const sec = (ms: number | null) => (ms == null ? null : Math.floor(ms / 1000));
  const exit = exitOf(t);
  const r = open ? t.r_multiple : closedTradeR(t);
  return {
    id: `mine-${t.id}`,
    source: "mine",
    action: t.direction,
    signalTime: sec(t.created_at)!,
    entry: t.entry_price,
    stopLoss: t.stop_loss,
    tp1: t.take_profit_1,
    tp2: t.take_profit_2 ?? t.take_profit_1,
    riskReward: Math.abs(t.take_profit_1 - t.entry_price) / (Math.abs(t.entry_price - t.stop_loss) || 1),
    archetype: null, quality: null, trigger: null, thesis: null, score: null,
    timeStopHours: t.time_stop_hours,
    againstBtc: (t.risk_scale ?? 1) < 1,
    result,
    fillTime: sec(t.created_at),
    tp1Time: sec(t.tp1_hit_at),
    exitTime: open ? null : sec(t.closed_at),
    exitPrice: open ? null : exit,
    r: r == null ? null : Math.round(r * 100) / 100,
  };
}

/** What a new trade ticket starts with: the engine's plan when there is one. */
export interface TicketDefaults {
  entryType: EntryType;
  /** Why it counts as that type, in plain words. */
  reason: string;
  entry: number;
  stop: number;
  tp1: number;
  tp2: number;
  timeStopHours: number;
  archetype: string | null;
  /** Share of the usual risk to size it for: AGAINST_BTC_RISK on an Against-BTC signal, else 1. */
  riskScale: number;
}

/** Plan time limits by setup (backend setupClassifier.ts, 1h). */
const TIME_STOP_HOURS: Record<string, number> = { trend_pullback: 24, range_fade: 36 };

const roundPrice = (n: number) => Number(n.toPrecision(6));

/**
 * Pre-fill a trade ticket from what the engine says about the coin now:
 * a Ready or live Telegram signal on this side → its stop and targets ("signal");
 * a Waiting/Almost zone on this side → the zone's would-be plan ("early");
 * otherwise a 1.5 ATR stop with targets at 1.5× and 2.5× the risk ("own").
 * Entry is always the price now: that's where the trade starts.
 */
export function ticketDefaults(
  side: "LONG" | "SHORT",
  price: number,
  row: DeskRow | null | undefined,
  openSignal: OpenSignal | null | undefined,
  atr: number | null,
): TicketDefaults {
  const long = side === "LONG";
  const word = long ? "buy" : "sell";
  const plan = (p: { stopLoss: number; tp1: number; tp2: number }, hours: number) => ({
    entry: roundPrice(price), stop: roundPrice(p.stopLoss), tp1: roundPrice(p.tp1), tp2: roundPrice(p.tp2), timeStopHours: hours,
  });

  const halfNote = (against: boolean | undefined) =>
    against ? ` It goes against BTC's trend, so it is sized at half your usual risk.` : "";
  if (row?.signal && row.signal.action === side) {
    const s = row.signal;
    return {
      entryType: "signal",
      reason: `The engine's ${archetypeLabel(s.setupArchetype)} ${word} signal on the last 1-hour close. Using its stop and targets.${halfNote(s.againstBtc)}`,
      archetype: s.setupArchetype,
      riskScale: s.againstBtc ? AGAINST_BTC_RISK : 1,
      ...plan(s.plan, s.plan.timeStopHours),
    };
  }
  if (openSignal && openSignal.action === side && (openSignal.state === "valid" || openSignal.state === "in-profit")) {
    return {
      entryType: "signal",
      reason: `Following the ${word} signal sent to Telegram ${ago(openSignal.sentAt)}. Using its stop and targets.${halfNote(openSignal.againstBtc)}`,
      archetype: null,
      riskScale: openSignal.againstBtc ? AGAINST_BTC_RISK : 1,
      ...plan(openSignal.plan, openSignal.plan.timeStopHours),
    };
  }
  const zone = deskSetupFor(row);
  if (zone?.plan && zone.direction === side) {
    return {
      entryType: "early",
      reason: `No signal yet: the engine is ${zone.armed ? "Almost" : "Waiting"} in a ${word} zone (${archetypeLabel(zone.details.archetype)}). This counts as an early entry, tracked apart from signals.`,
      archetype: zone.details.archetype,
      riskScale: 1,
      ...plan({ stopLoss: zone.plan.stop, tp1: zone.plan.tp1, tp2: zone.plan.tp2 }, TIME_STOP_HOURS[zone.details.archetype] ?? 24),
    };
  }

  const stale = openSignal && openSignal.action === side ? `The Telegram ${word} signal is ${openSignal.state}, so ` : "";
  const against = row && row.checklist.direction !== side && (row.verdict.stage === "stalking" || row.verdict.stage === "armed")
    ? `The engine is looking to ${long ? "sell" : "buy"} this coin, not ${word}: `
    : "";
  const risk = atr && atr > 0 ? 1.5 * atr : price * 0.01;
  const dir = long ? 1 : -1;
  return {
    entryType: "own",
    reason: `${stale || against || `No engine ${word} setup on this coin now: `}this counts as your own idea. Stop 1.5 ATR away, targets at 1.5× and 2.5× the risk: change them to your plan.`,
    archetype: null,
    riskScale: 1,
    ...plan({ stopLoss: price - dir * risk, tp1: price + dir * 1.5 * risk, tp2: price + dir * 2.5 * risk }, 24),
  };
}

/**
 * Your logged trades and the win/loss summary for a period. Refreshes every
 * 30 s (the tracker grades trades every 2 minutes) and on `refresh()`.
 */
export function useMyTrades(period: SummaryPeriod) {
  const [trades, setTrades] = useState<TrackedTrade[]>([]);
  const [summary, setSummary] = useState<TradeSummary | null>(null);
  /** Start of the period (epoch ms): today = the risk guard's trading day. */
  const [from, setFrom] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const [list, sum] = await Promise.all([
        fetch("/api/tracked-trades").then(r => r.json()),
        // Your own trades; the engine's auto-tracked signals come back in bySource.
        fetch(`/api/tracked-trades/summary?period=${period}&source=manual`).then(r => r.json()),
      ]);
      if (!list.success || !sum.success) throw new Error(list.error || sum.error || "Trades unavailable");
      setTrades((list.trades as TrackedTrade[]).filter(t => !isAutoTrade(t)));
      setSummary({ all: sum.all, byType: sum.byType, byMode: sum.byMode, bySource: sum.bySource });
      setFrom(sum.from);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Trades unavailable");
    }
  }, [period]);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 30_000);
    // Any part of the app that logs a trade fires this, so every view updates.
    window.addEventListener(TRADES_CHANGED, refresh);
    return () => { clearInterval(id); window.removeEventListener(TRADES_CHANGED, refresh); };
  }, [refresh]);

  return { trades, summary, from, error, refresh };
}

/** Window event fired after a trade is logged or closed. */
export const TRADES_CHANGED = "vp:trades";
